import { useMemo, useState } from 'react';

import { useResolvedMediaUrl } from '@/hooks/use-media-url';
import { classNames } from '@/utils/classNames';

import styles from '@/styles/common.module.css';

type CandidateAvatarProps = {
  alt: string;
  imageUrl: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

const sizeClassName: Record<NonNullable<CandidateAvatarProps['size']>, string> = {
  sm: styles.avatarSmall!,
  md: styles.avatarMedium!,
  lg: styles.avatarLarge!,
};

export function CandidateAvatar({
  alt,
  imageUrl,
  name,
  size = 'md',
  className,
}: CandidateAvatarProps): JSX.Element {
  const [hasError, setHasError] = useState(false);
  const resolvedImageUrl = useResolvedMediaUrl(imageUrl);

  const initials = useMemo(() => {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase();
  }, [name]);

  const canRenderImage =
    resolvedImageUrl.status === 'success' &&
    resolvedImageUrl.resolvedUrl.length > 0 &&
    !hasError;

  return (
    <div className={classNames(styles.avatar, sizeClassName[size], className)}>
      {canRenderImage ? (
        <img
          className={styles.avatarImage}
          src={resolvedImageUrl.resolvedUrl}
          alt={alt}
          onError={() => setHasError(true)}
        />
      ) : (
        <span className={styles.avatarFallback} aria-hidden="true">
          {initials}
        </span>
      )}
    </div>
  );
}
